import { useDatabase, withObservables } from "@nozbe/watermelondb/react";
import { ProductLocationInventoryPricing } from "@towersystems/roam-common/lib/generated-types";
import Decimal from "decimal.js";
import { useEffect, useState } from "react";
import { useActiveLocation } from "../../../../../../utilities/app-state/hooks";
import { CartItemRenderer } from "./components/CartItemRenderer";
import { CartItemProps, OverridesType } from "./types";
import { getObservables, resolveSaleLocationPricing } from "./utils";
import { CartItemProductDetails } from "./components/CartItemProductDetails";
import ProductKitItem from "../../../../../../utilities/data/model/ProductKitItem";
import Product from "../../../../../../utilities/data/model/Product";
import {
  ProductThumbnail,
  Stack,
  useTheme,
  View,
} from "@towersystems/roam-common-native";
import { Spacings } from "react-native-ui-lib";
import { toInternalProductType } from "../../../../../../utilities/to-internal-product-type";
import { fetchSaleKitItemDetails } from "../../../../utils/sale-item/utils";

const CartItemContainer = ({ product, item }: CartItemProps) => {
  const { location } = useActiveLocation();
  const database = useDatabase();
  const { colors: Colors } = useTheme();

  const [overrides, setOverrides] = useState<OverridesType | undefined>(
    product.isKit
      ? {
          isLoading: true,
          data: {
            totalPrice: item.totalPrice,
            totalBasePrice: item.totalBasePrice,
            hasAdjustments: item.hasAdjustments,
            kitItemDetails: [],
          },
          isError: false,
        }
      : undefined
  );

  async function fetchProductKitOverrides() {
    const productKit = await product.productKit;

    const kitItemDetails = await fetchSaleKitItemDetails(
      database,
      productKit,
      item
    );

    const totalBasePrice = kitItemDetails.reduce(
      (total, { kitItem, product: resolvedProduct }) => {
        const pricing: ProductLocationInventoryPricing | undefined =
          resolveSaleLocationPricing(location, resolvedProduct);

        let resolvedBasePrice = pricing?.retailPrice || 0;

        const totalComponentPrice = new Decimal(resolvedBasePrice).mul(
          new Decimal(kitItem.quantity)
        );
        return total.add(totalComponentPrice);
      },
      new Decimal(0)
    );

    const totalPrice = item.totalPrice;
    const totalBasePriceNumber = totalBasePrice.toNumber();

    const hasAdjustments =
      !!totalBasePriceNumber && totalPrice < totalBasePriceNumber;

    return {
      totalPrice,
      totalBasePrice: totalBasePriceNumber,
      hasAdjustments: hasAdjustments,
      kitItemDetails,
    };
  }

  useEffect(() => {
    if (!product.isKit) {
      return;
    }

    fetchProductKitOverrides().then((newOverrides) => {
      setOverrides({
        isLoading: false,
        data: newOverrides,
        isError: false,
      });
    });
  }, [product, JSON.stringify(item.adjustments || []), item]);

  const kitItemsMarkup = !!overrides?.data?.kitItemDetails.length ? (
    <Stack vertical spacing={Spacings.s3}>
      {overrides.data?.kitItemDetails.map((kitItemDetail) => {
        const item = kitItemToSaleItem(
          location,
          kitItemDetail.kitItem,
          kitItemDetail.product
        );

        return (
          <View
            key={`kit-item-${kitItemDetail.kitItem.id}`}
            style={{
              alignItems: "center",
              flexBasis: "100%",
              flexShrink: 1,
            }}
            row
          >
            <View style={{ marginRight: Spacings.s4 }}>
              <ProductThumbnail
                imageUrl={product.imageUrl}
                isInternalProduct={
                  toInternalProductType(product) && product?.internal
                }
                internalProductType={toInternalProductType(product)}
              />
            </View>
            <CartItemProductDetails
              tintColor={Colors.text}
              product={kitItemDetail.product}
              item={item}
            />
          </View>
        );
      })}
    </Stack>
  ) : null;

  return (
    <CartItemRenderer {...{ product, item, overrides }}>
      {kitItemsMarkup}
    </CartItemRenderer>
  );
};

function kitItemToSaleItem(
  activeLocation: any,
  kitItem: ProductKitItem,
  product: Product
): any {
  const pricing: ProductLocationInventoryPricing | undefined =
    resolveSaleLocationPricing(activeLocation, product);

  let resolvedBasePrice = pricing?.retailPrice || 0;

  const taxRatePercent = pricing?.taxRatePercent || 0;

  const quantity = kitItem.quantity || 1;

  const discount = kitItem.price - resolvedBasePrice;

  const name = `${kitItem.quantity} × ${product.displayName || product.name}`;

  return {
    name: name,
    productId: product.id,
    quantity: quantity,
    basePrice: resolvedBasePrice,
    discount: discount,
    taxRatePercent: taxRatePercent,
  };
}

export default withObservables(["item"], getObservables)(CartItemContainer);
